/**
 * The API's content version, learned from the `X-Content-Version` response header.
 *
 * Content URLs carry this version as `?v=`, which lets the API serve them as immutable: they then sit
 * in the browser and CDN caches instead of crossing to the origin on every read. An import or a
 * deletion moves the version, so clients start asking for different URLs rather than for a newer copy
 * of the old ones — which is what stands in for a cache purge, since the CDN in front of the API
 * belongs to the host and cannot be purged by us.
 *
 * It is kept in localStorage so a returning visitor can pin its first requests immediately, without
 * waiting to be told the version again.
 */

const CONTENT_VERSION_KEY = 'byble_content_version';

let current = localStorage.getItem(CONTENT_VERSION_KEY) ?? '';
const listeners = new Set<() => void>();

export function getContentVersion(): string {
  return current;
}

/**
 * Records a version seen on a response. Ignores anything empty or unchanged, so this can be called
 * from every request without waking subscribers for nothing.
 */
export function setContentVersion(version: string | null): void {
  if (!version || version === current) return;

  current = version;
  localStorage.setItem(CONTENT_VERSION_KEY, version);
  listeners.forEach((listener) => listener());
}

export function subscribeContentVersion(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The `?v=` suffix for a content URL. Empty while the version is still unknown — the API then falls
 * back to serving the URL with revalidation, so requests stay correct, just not cacheable.
 */
export function versionParam(version: string): string {
  return version ? `?v=${encodeURIComponent(version)}` : '';
}
